/* Filter Configuration file
 *
 * filters informations :
 *   for each filter you must have :
 *     a name           : specify the filter name, use as value in filter selector option
 *     a type           : specify the filter type, it's explain after.
 *     a label          : specify the filter label, use as innerHTML in filter selector option
 *     a format         : specify how to build filter type with subfilters params
 *     a subFilters     : Array that can contain ( or not ) more usable sub filters
 *     a criterionTypes : Array that can contain ( or not ) more criterion types
 *   each filter can have additional parameters base on it's type
 *
 * filters type informations :
 *  [completion]
 *     decription: filter with suggestion list based on typed user value
 *     params:
 *       name: in lowercase will be send to searchControllers
 *  [confidence]
 *     filter for confidence, has it's own type cause it's note a regular case
 *  [free]
 *     description : free text input filter
 *  [range]
 *     description: range filter
 *     params:
 *       less: specify if value can be less than min
 *       min: minimum range value
 *       interval: specify interval between step in the slider
 *       max: maximum range value
 *       more: specify if value can me more than max
 *       unit: specify unity
 *  [remoteList]
 *    description: select based on database value
 *    params:
 *      listPath: name send to search controller to get the list
 *      listKey: field name of the object to use for value
 *  [select]
 *    description: select fix value
 *    params:
 *      options: array with each option value
 *      default: default select value
 *      labels: object that specify labels for each key, if one is not present value will be use as label
 *
 */
import {
  AdmissionModeCh,
  AdmissionModeFrMco,
  AdmissionReleaseModeFr,
  ReleaseModeCh,
  ReleaseModeFr
} from '@sancare/typescript-commons/src/types/admission-release-mode'
import { DestinationFr, OriginFr } from '@sancare/typescript-commons/src/types/origin-destination'

import { documentCategories, textualHealthDataCategories } from '@/stay-displayer/health-data/documentLabels'
import { AutomationTypeEnum } from '@/store/modules/automation/types'
import { FilterData, FilterDataTypeEnum, FilterGroupEnum, FilterValueTypeEnum } from '@/store/modules/filters/types'
import { CountryEnum, ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

export function isGlobalCriteria(conditionType: string): boolean {
  return conditionType.startsWith('revalo') || conditionType.startsWith('qaAbsent') || conditionType.startsWith('previousStay') || ['softAbsentContent', 'exclusiveMedicalAct'].includes(conditionType)
}

interface OptionFilter {
  country?: CountryEnum
  pmsiType?: PmsiTypeEnum
}
const createFilteredOptionsArray = (values: { [key: string]: string }, { country, pmsiType }: OptionFilter) => {
  return Object.keys(values).map((key) => ({
    value: key,
    label: values[key],
    country,
    pmsiType
  }))
}

export default {
  content: {
    name: 'content',
    type: FilterDataTypeEnum.TEXT_CONTENT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'both',
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.content',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{presence}Content',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent', 'softAbsent'],
        isGlobalOptions: ['softAbsent'],
        notGlobalOptions: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Mots-clés présents',
          absent: 'Passages exclus',
          softAbsent: 'Passages exclus des mots-clés'
        }
      }
    ]
  },
  title: {
    name: 'title',
    type: FilterDataTypeEnum.TEXT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.title',
    country: [CountryEnum.CH, CountryEnum.FR],
    subFilters: [],
    format: 'presentTitle',
  },
  healthConstant: {
    name: 'healthConstant',
    type: FilterDataTypeEnum.CONSTANT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.healthConstant',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{part}HealthConstant',
    subFilters: [
      {
        name: 'part',
        type: FilterDataTypeEnum.SELECT,
        options: ['equalTo', 'greaterThan', 'lessThan'],
        default: 'equal',
        labels: {
          equalTo: 'Egale à',
          greaterThan: 'Supérieure à',
          lessThan: 'Inférieure à',
        }
      }
    ]
  },
  drug: {
    name: 'drug',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'drug',
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.drug',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{presence}Drug',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  biology: {
    name: 'biology',
    type: FilterDataTypeEnum.BIOLOGY,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.biology',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'biologyResult',
  },
  diagnosis: {
    name: 'diagnosis',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'diagnosis',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    automationTypes: [],
    isTranslated: true,
    label: 'filter.labels.diagnosis',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{presence}{etabType}{diagType}Diagnosis',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
      {
        name: 'etabType',
        type: FilterDataTypeEnum.SELECT,
        options: ['etab', 'sancare', 'both'],
        default: 'etab',
        labels: {
          etab: 'Etablissement',
          sancare: 'Sancare',
          both: 'L\'un ou l\'autre',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.MCO],
        name: 'diagType',
        country: [CountryEnum.FR],
        type: FilterDataTypeEnum.SELECT,
        options: ['global', 'principal', 'related', 'associated'],
        default: 'global',
        isTranslated: true,
        labels: {
          global: 'diagnosis.all',
          principal: 'diagnosis.DP',
          related: 'diagnosis.DR',
          associated: 'diagnosis.DAS',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.MCO],
        name: 'diagType',
        country: [CountryEnum.CH],
        type: FilterDataTypeEnum.SELECT,
        options: ['global', 'principal', 'associated'],
        default: 'global',
        isTranslated: true,
        labels: {
          global: 'diagnosis.all',
          principal: 'diagnosis.DP',
          associated: 'diagnosis.DAS',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.SMR],
        name: 'diagType',
        type: FilterDataTypeEnum.SELECT,
        options: ['global', 'MMP', 'AE', 'DAS'],
        default: 'global',
        isTranslated: true,
        labels: {
          global: 'diagnosis.all',
          MMP: 'diagnosis.MMP',
          AE: 'diagnosis.AE',
          DAS: 'diagnosis.DAS',
        },
      },
    ],
  },
  revaloDiagnosis: {
    name: 'revaloDiagnosis',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'diagnosis',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    mode: [ModeEnum.OPTIMIZABLE],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.revaloDiagnosis',
    country: [CountryEnum.FR],
    format: 'revalo{diagType}',
    subFilters: [
      {
        pmsiTypes: [PmsiTypeEnum.MCO],
        name: 'diagType',
        type: FilterDataTypeEnum.SELECT,
        // options: ['DAS', 'DP], // hide DP for now (UI-3194), see https://app.clickup.com/t/2628858/UI-3340
        options: ['DAS'],
        default: 'DAS',
        isTranslated: true,
        labels: {
          DP: 'diagnosis.DP',
          DAS: 'diagnosis.DAS',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.SMR],
        name: 'diagType',
        type: FilterDataTypeEnum.SELECT,
        options: ['DAS'],
        default: 'DAS',
        labels: {
          DAS: 'DAS',
        },
      },
    ],
  },
  qaAbsentDiagnosis: {
    name: 'qaAbsentDiagnosis',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'diagnosis',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    mode: [ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.qaAbsentDiagnosis',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'qaAbsent{diagType}',
    subFilters: [
      {
        pmsiTypes: [PmsiTypeEnum.MCO],
        name: 'diagType',
        country: [CountryEnum.FR],
        type: FilterDataTypeEnum.SELECT,
        options: ['DAS', 'DP', 'DR'],
        default: 'DAS',
        isTranslated: true,
        labels: {
          DP: 'diagnosis.DP',
          DR: 'diagnosis.DR',
          DAS: 'diagnosis.DAS',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.MCO],
        name: 'diagType',
        country: [CountryEnum.CH],
        type: FilterDataTypeEnum.SELECT,
        options: ['DAS', 'DP'],
        default: 'DAS',
        isTranslated: true,
        labels: {
          DP: 'diagnosis.DP',
          DAS: 'diagnosis.DAS',
        },
      },
      {
        pmsiTypes: [PmsiTypeEnum.SMR],
        name: 'diagType',
        type: FilterDataTypeEnum.SELECT,
        options: ['DAS', 'MMP', 'AE'],
        default: 'DAS',
        labels: {
          DAS: 'DAS',
          MMP: 'MMP',
          AE: 'AE',
        },
      },
    ],
  },
  stayDate: {
    name: 'stayDate',
    type: FilterDataTypeEnum.DATE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.stayDate',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{dateType}StayDate',
    subFilters: [
      {
        name: 'dateType',
        type: FilterDataTypeEnum.SELECT,
        options: ['from', 'to'],
        default: 'from',
        labels: {
          from: 'À partir du',
          to: 'Jusqu\'au',
        },
      },
    ],
  },
  lastSurgeryDate: {
    name: 'lastSurgeryDate',
    type: FilterDataTypeEnum.DATE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.fromLastSurgeryDate',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{dateType}LastSurgeryDate',
    subFilters: [
      {
        name: 'dateType',
        type: FilterDataTypeEnum.SELECT,
        options: ['from', 'to'],
        default: 'from',
        labels: {
          from: 'À partir du',
          to: 'Jusqu\'au',
        },
      },
    ],
  },
  compareDiagnosis: {
    name: 'compareDiagnosis',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.NEW_CODING, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.compareDiagnosis',
    country: [CountryEnum.CH, CountryEnum.FR],
    options: ['identical', 'different'],
    default: '',
    format: 'compareDiagnosis',
    labels: {
      identical: 'Identique',
      different: 'Différent',
    },
  },
  medicalActActivity: {
    name: 'medicalActActivity',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.medicalActActivity',
    country: [CountryEnum.FR],
    options: ['1', '2', '3', '4', '5'],
    default: '',
    format: 'medicalActActivity',
    labels: {
      1: '1',
      2: '2',
      3: '3',
      4: '4',
      5: '5'
    },
  },
  admissionMode: {
    name: 'admissionMode',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.admissionMode',
    country: [CountryEnum.CH, CountryEnum.FR],
    options: [
      ...createFilteredOptionsArray(AdmissionReleaseModeFr, { country: CountryEnum.FR }),
      ...createFilteredOptionsArray(AdmissionModeFrMco, { country: CountryEnum.FR, pmsiType: PmsiTypeEnum.MCO }),
      ...createFilteredOptionsArray(AdmissionModeCh, { country: CountryEnum.CH }),
    ],
    optionFiltered: true,
    default: '',
    format: '{presence}AdmissionMode',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  releaseMode: {
    name: 'releaseMode',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.releaseMode',
    country: [CountryEnum.CH, CountryEnum.FR],
    options: [
      ...createFilteredOptionsArray(ReleaseModeFr, { country: CountryEnum.FR }),
      ...createFilteredOptionsArray(ReleaseModeCh, { country: CountryEnum.CH }),
    ],
    optionFiltered: true,
    default: '',
    format: '{presence}ReleaseMode',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  medicalAct: {
    name: 'medicalAct',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'medicalAct',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    label: 'medicalAct.title',
    country: [CountryEnum.CH, CountryEnum.FR],
    isTranslated: true,
    isPlurial: true,
    format: '{presence}MedicalAct',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  qaAbsentMedicalAct: {
    name: 'qaAbsentMedicalAct',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'medicalAct',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    mode: [ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    isPlurial: true,
    label: 'filter.labels.qaAbsentMedicalAct',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'qaAbsentMedicalAct',
  },
  exclusiveMedicalAct: {
    name: 'exclusiveMedicalAct',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'medicalAct',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    // mode: [ModeEnum.AUTOMATION],
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.exclusiveMedicalAct',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'exclusiveMedicalAct',
  },
  previousStayDiagnosis: {
    name: 'previousStayDiagnosis',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'diagnosis',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    mode: [ModeEnum.AUTOMATION],
    automationTypes: [AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.previousStayDiagnosis',
    country: [CountryEnum.FR],
    format: 'previousStay{diagType}',
    subFilters: [
      {
        name: 'diagType',
        type: FilterDataTypeEnum.SELECT,
        options: ['DP', 'DR', 'DAS'],
        default: 'DP',
        isTranslated: true,
        labels: {
          DP: 'diagnosis.DP',
          DR: 'diagnosis.DR',
          DAS: 'diagnosis.DAS',
        },
      },
    ],
  },
  previousStayMedicalUnit: {
    name: 'previousStayMedicalUnit',
    type: FilterDataTypeEnum.MEDICAL_UNIT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    isGlobalCondition: 'true',
    mode: [ModeEnum.AUTOMATION],
    automationTypes: [AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.previousStayMedicalUnit',
    country: [CountryEnum.FR],
    format: 'previousStayMedicalUnit',
    criterionTypes: ['previousStayMedicalUnit'],
  },
  rehabilitationAct: {
    name: 'rehabilitationAct',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'rehabilitationAct',
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.SMR],
    label: 'rehabilitationAct.title',
    country: [CountryEnum.FR],
    isTranslated: true,
    isPlurial: true,
    format: '{presence}RehabilitationAct',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  lastRhsRehabilitationActCount: {
    name: 'lastRhsRehabilitationActCount',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.lastRhsRehabilitationActCount',
    country: [CountryEnum.FR],
    format: 'lastRhsRehabilitationActCount',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 10,
      more: true,
      unit: '',
    }
  },
  startingGhm: {
    name: 'startingGhm',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'ghm',
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.startingGhm',
    country: [CountryEnum.FR],
    format: '{presence}StartingGhm',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  currentGhm: {
    name: 'currentGhm',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'ghm',
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.currentGhm',
    country: [CountryEnum.FR],
    format: '{presence}CurrentGhm',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  startingLevel: {
    name: 'startingLevel',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.startingLevel',
    country: [CountryEnum.FR],
    options: ['1', '2', '3', '4', 'A', 'B', 'C', 'D', 'E', 'J', 'T', 'Z'],
    default: '1',
    format: '{presence}StartingGhmLevel',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  currentLevel: {
    name: 'currentLevel',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.currentLevel',
    country: [CountryEnum.FR],
    options: ['1', '2', '3', '4', 'A', 'B', 'C', 'D', 'E', 'J', 'T', 'Z'],
    default: '1',
    format: '{presence}CurrentGhmLevel',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  startingGhmType: {
    name: 'startingGhmType',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.startingGhmType',
    country: [CountryEnum.FR],
    options: ['C', 'M', 'K', 'Z'],
    format: '{presence}StartingGhmType',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  currentGhmType: {
    name: 'currentGhmType',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.currentGhmType',
    country: [CountryEnum.FR],
    options: ['C', 'M', 'K', 'Z'],
    format: '{presence}CurrentGhmType',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  medicalUnit: {
    name: 'medicalUnit',
    type: FilterDataTypeEnum.MEDICAL_UNIT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.medicalUnit',
    country: [CountryEnum.FR],
    format: 'medicalUnit',
    criterionTypes: [
      'presentMedicalUnit',
      'absentMedicalUnit',
      'presentDivision',
      'absentDivision',
      'presentFacility',
      'absentFacility',
    ],
  },
  dmsGap: {
    name: 'dmsGap',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.dmsGap',
    country: [CountryEnum.FR],
    format: 'startingDmsGap',
    range: {
      less: true,
      min: -10,
      interval: 1,
      max: 10,
      more: true,
      unit: 'j',
    },
  },
  stayDuration: {
    name: 'stayDuration',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.stayDuration',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'stayDuration',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 20,
      more: true,
      unit: 'j'
    }
  },
  rumCount: {
    name: 'rumCount',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO], // todo https://app.clickup.com/t/86bydp4fz
    isTranslated: true,
    label: 'filter.labels.rumCount',
    country: [CountryEnum.FR],
    format: 'rumCount',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 10,
      more: true,
      unit: '',
    }
  },
  patientAge: {
    name: 'patientAge',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.PATIENT,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.patientAge',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'patientAge',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 100,
      more: true,
      unit: 'ans',
    },
  },
  // confidence: {
  //   name: 'confidence',
  //   type: 'confidence',
  //   valueType: FilterValueTypeEnum.SINGLE_VALUE,
  //   mode: [], /* DISABLED FOR THE MOMENT */
  //   label: 'Taux de confiance',
  //   country: [CountryEnum.FR],
  //   range: {
  //     less: false,
  //     min: 0,
  //     interval: 1,
  //     max: 100,
  //     more: false,
  //     unit: '%',
  //   },
  // },
  // moneyGap: {
  //   name: 'moneyGap',
  //   type: FilterDataTypeEnum.RANGE,
  //   valueType: FilterValueTypeEnum.SINGLE_VALUE,
  //   mode: [], // TODO clearly set this data point
  //   label: 'Écart de tarifs',
  //   country: [CountryEnum.FR],
  //   format: 'moneyGap',
  //   range: {
  //     less: true,
  //     min: -5000,
  //     interval: 50,
  //     max: 5000,
  //     more: true,
  //     unit: '€',
  //   },
  // },
  gender: {
    name: 'gender',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.PATIENT,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.gender',
    format: 'gender',
    country: [CountryEnum.CH, CountryEnum.FR],
    options: ['1', '2', '3'],
    labels: {
      1: 'Homme',
      2: 'Femme',
      3: 'Indéterminé',
    },
  },
  hasSession: {
    name: 'hasSession',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.hasSession',
    country: [CountryEnum.FR],
    format: 'hasSession',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  hasTextualDocuments: {
    name: 'hasTextualDocuments',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.RULES, AutomationTypeEnum.AI],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.hasTextualDocuments',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'hasTextualDocuments',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  hasDrugEvents: {
    name: 'hasDrugEvents',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.RULES, AutomationTypeEnum.AI],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.hasDrugEvents',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'hasDrugEvents',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  hasLabResults: {
    name: 'hasLabResults',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.RULES, AutomationTypeEnum.AI],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.hasLabResults',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'hasLabResults',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  hasHealthConstants: {
    name: 'hasHealthConstants',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.RULES, AutomationTypeEnum.AI],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.hasHealthConstants',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: 'hasHealthConstants',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  documentCategory: {
    name: 'documentCategory',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.DOCUMENTS,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.documentCategory',
    country: [CountryEnum.CH, CountryEnum.FR],
    format: '{presence}DocumentCategory',
    options: Object.keys({ ...documentCategories, ...textualHealthDataCategories }),
    labels: { ...documentCategories, ...textualHealthDataCategories },
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        }
      }
    ]
  },
  origin: {
    name: 'origin',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.origin',
    country: [CountryEnum.FR],
    options: [
      ...createFilteredOptionsArray(OriginFr, { country:CountryEnum.FR }),
    ],
    optionFiltered: true,
    default: '',
    format: '{presence}Origin',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  destination: {
    name: 'destination',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO, PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.destination',
    country: [CountryEnum.FR],
    options: [
      ...createFilteredOptionsArray(DestinationFr, { country:CountryEnum.FR }),
    ],
    optionFiltered: true,
    default: '',
    format: '{presence}Destination',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  initialGme: {
    name: 'initialGme',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'gme',
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.initialGme',
    country: [CountryEnum.FR],
    format: '{presence}InitialGme',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  currentGme: {
    name: 'currentGme',
    type: FilterDataTypeEnum.COMPLETION,
    completionName: 'gme',
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.MULTI_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.currentGme',
    country: [CountryEnum.FR],
    format: '{presence}CurrentGme',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        options: ['present', 'absent'],
        default: 'present',
        labels: {
          present: 'Présent',
          absent: 'Absent',
        },
      },
    ],
  },
  dependency: {
    name: 'dependency',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.CODING,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.dependency',
    country: [CountryEnum.FR],
    multiValueRanges: [
      {
        values: [
          'dressing',
          'moving',
          'feeding',
          'continence',
          'behaviour',
          'social'
        ],
        range: {
          less: false,
          min: 1,
          interval: 1,
          max: 4,
          more: false,
          unit: '',
        }
      }, {
        values: ['physical'],
        range: {
          less: false,
          min: 0,
          interval: 1,
          max: 16,
          more: false,
          unit: '',
        }
      }, {
        values: ['cognitive'],
        range: {
          less: false,
          min: 0,
          interval: 1,
          max: 8,
          more: false,
          unit: '',
        }
      },
    ],
    format: '{presence}Dep{depType}',
    subFilters: [
      {
        name: 'presence',
        type: FilterDataTypeEnum.SELECT,
        isTranslated: true,
        options: ['absent', 'present'],
        default: 'present',
        labels: {
          absent: 'filter.labels.absence',
          present: 'filter.labels.presence',
        },
      },
      {
        name: 'depType',
        type: FilterDataTypeEnum.SELECT,
        isTranslated: true,
        options: [
          {
            value: 'physical',
            isTitle: true,
          },
          {
            value: 'dressing',
            isTitle: false,
          },
          {
            value: 'moving',
            isTitle: false,
          },
          {
            value: 'feeding',
            isTitle: false,
          },
          {
            value: 'continence',
            isTitle: false,
          },
          {
            value: 'cognitive',
            isTitle: true,
          },
          {
            value: 'behaviour',
            isTitle: false,
          },
          {
            value: 'social',
            isTitle: false,
          },
        ],
        default: '',
        labels: {
          physical: 'dependency.depPhysical',
          dressing: 'dependency.depDressing',
          moving: 'dependency.depMoving',
          feeding: 'dependency.depFeeding',
          continence: 'dependency.depContinence',
          cognitive: 'dependency.depCognitive',
          behaviour: 'dependency.depBehaviour',
          social: 'dependency.depSocial',
        },
      },
    ],
  },
  qaAbsentDependency: {
    name: 'qaAbsentDependency',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isGlobalCondition: 'true',
    isTranslated: true,
    label: 'filter.labels.qaAbsentDependency',
    country: [CountryEnum.FR],
    multiValueRanges: [
      {
        values: [
          'dressing',
          'moving',
          'feeding',
          'continence',
          'behaviour',
          'social'
        ],
        range: {
          less: false,
          min: 1,
          interval: 1,
          max: 4,
          more: false,
          unit: '',
        }
      }, {
        values: ['physical'],
        range: {
          less: false,
          min: 0,
          interval: 1,
          max: 16,
          more: false,
          unit: '',
        }
      }, {
        values: ['cognitive'],
        range: {
          less: false,
          min: 0,
          interval: 1,
          max: 8,
          more: false,
          unit: '',
        }
      },
    ],
    format: 'qaAbsentDep{depType}',
    subFilters: [
      {
        name: 'depType',
        type: FilterDataTypeEnum.SELECT,
        isTranslated: true,
        options: [
          {
            value: 'physical',
            isTitle: true,
          },
          {
            value: 'dressing',
            isTitle: false,
          },
          {
            value: 'moving',
            isTitle: false,
          },
          {
            value: 'feeding',
            isTitle: false,
          },
          {
            value: 'continence',
            isTitle: false,
          },
          {
            value: 'cognitive',
            isTitle: true,
          },
          {
            value: 'behaviour',
            isTitle: false,
          },
          {
            value: 'social',
            isTitle: false,
          },
        ],
        default: '',
        labels: {
          physical: 'dependency.depPhysical',
          dressing: 'dependency.depDressing',
          moving: 'dependency.depMoving',
          feeding: 'dependency.depFeeding',
          continence: 'dependency.depContinence',
          cognitive: 'dependency.depCognitive',
          behaviour: 'dependency.depBehaviour',
          social: 'dependency.depSocial',
        },
      },
    ],
  },
  rehabilitationActCount: {
    name: 'rehabilitationActCount',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    label: 'filter.labels.rehabilitationActCount',
    country: [CountryEnum.FR],
    isTranslated: true,
    format: 'rehabilitationActCount',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 30,
      more: true,
      unit: '',
    }
  },
  medicalActCount: {
    name: 'medicalActCount',
    type: FilterDataTypeEnum.RANGE,
    filterGroup: FilterGroupEnum.ACTS_AND_DIAGNOSES,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    label: 'filter.labels.medicalActCount',
    isTranslated: true,
    country: [CountryEnum.FR],
    format: 'medicalActCount',
    range: {
      less: false,
      min: 0,
      interval: 1,
      max: 30,
      more: true,
      unit: '',
    }
  },
  isPostSurgical: {
    name: 'isPostSurgical',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS],
    automationTypes: [],
    pmsiTypes: [PmsiTypeEnum.SMR],
    isTranslated: true,
    label: 'filter.labels.isPostSurgical',
    country: [CountryEnum.FR],
    format: 'isPostSurgical',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
  isRaac: {
    name: 'isRaac',
    type: FilterDataTypeEnum.SELECT,
    filterGroup: FilterGroupEnum.STAY,
    valueType: FilterValueTypeEnum.SINGLE_VALUE,
    mode: [ModeEnum.AUTOMATION, ModeEnum.NEW_CODING, ModeEnum.OPTIMIZABLE, ModeEnum.QUALITY_STAYS, ModeEnum.DIALYSIS],
    automationTypes: [AutomationTypeEnum.AI, AutomationTypeEnum.RULES],
    pmsiTypes: [PmsiTypeEnum.MCO],
    isTranslated: true,
    label: 'filter.labels.isRaac',
    country: [CountryEnum.FR],
    format: 'isRaac',
    options: ['0', '1'],
    labels: {
      0: 'Non',
      1: 'Oui',
    },
  },
} as { [key: string]: FilterData }
